import { ReportsRopaProcess } from "../Process"
import { 
    Box, 
    Typography, 
    Paper, 
    Grid, 
    Avatar
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import SecurityIcon from '@mui/icons-material/Security';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FILTER } from '../filter-types';

export const ReportsRopaArea = ({area, filter, process, data, category, legalBase, processing, position}) => {
    const { t } = useTranslation('mapping');
    // Calcular estatísticas da área
    const totalProcesses = area.processes?.length || 0;
    const totalData = area.processes?.reduce((acc, proc) => 
        acc + (proc.dataMapping?.length || 0), 0) || 0;
    const totalProcessing = area.processes?.reduce((acc, proc) => 
        acc + proc.dataMapping?.reduce((dAcc, dm) => 
            dAcc + (dm.dataProcessing?.length || 0), 0) || 0, 0) || 0;

    // Filtrar processos
    const filteredProcesses = area.processes?.filter((processFilter) => {
        if (filter === FILTER.PROCESS && !!process) {
            return processFilter.name === process
        }
        if (filter === FILTER.DATA && !!data) {
            return processFilter.dataMapping.some((dataMap) => dataMap.name === data)
        }
        if (filter === FILTER.CATEGORY && !!category) {
            return processFilter.dataMapping.some((dataMap) => dataMap.category === category)
        }
        if (filter === FILTER.PROCESSING && !!processing) {
            return processFilter.dataMapping.some((dataMap) => {
                return dataMap.dataProcessing.some((dataProcess) => {
                    return (dataProcess.processing) === processing
                })
            })
        }
        if (filter === FILTER.LEGAL_BASE && !!legalBase) {
            return processFilter.dataMapping.some((dataMap) => {
                return dataMap.dataProcessing.some((dataProcess) => {
                    return (dataProcess.legalBase) === legalBase
                })
            })
        }
        if (filter === FILTER.POSITION && !!position) {
            return processFilter.dataMapping.some((dataMap) => {
                return dataMap.dataProcessing.some((dataProcess) => {
                    return (dataProcess.position) === position
                })
            })
        }
        return true
    });

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                <Paper 
                    elevation={2}
                    sx={{ 
                        mt: 3, 
                        mb: 2,
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    {/* Header da Área */}
                    <Box 
                        sx={{ 
                            p: 3, 
                            background: 'linear-gradient(135deg, #dce5f9 0%, #c5d6f7 100%)',
                            color: '#3a4a5a'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'rgba(58,74,90,0.15)', width: 48, height: 48, mr: 2 }}>
                                <FolderIcon sx={{ fontSize: 28, color: '#6b7999' }} />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="overline" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                                    {t('ropa.area.label')}
                                </Typography>
                                <Typography variant="h5" component="h2" fontWeight="bold">
                                    {area.name}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Responsável e Estatísticas */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Box 
                                    sx={{ 
                                        p: 2, 
                                        bgcolor: 'rgba(58,74,90,0.1)', 
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: 'rgba(58,74,90,0.15)', width: 40, height: 40 }}>
                                        <PersonIcon sx={{ color: '#6b7999' }} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                            {t('ropa.area.responsible')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="600">
                                            {area.user || t('ropa.area.notDefined')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Grid container spacing={1}>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'rgba(58,74,90,0.1)', borderRadius: 2 }}>
                                            <AccountTreeIcon sx={{ mb: 0.5, color: '#6b7999' }} />
                                            <Typography variant="h6" fontWeight="bold">
                                                {totalProcesses}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                {t('ropa.area.processes')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'rgba(58,74,90,0.1)', borderRadius: 2 }}>
                                            <DataUsageIcon sx={{ mb: 0.5, color: '#6b8a99' }} />
                                            <Typography variant="h6" fontWeight="bold">
                                                {totalData}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                {t('ropa.area.data')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'rgba(58,74,90,0.1)', borderRadius: 2 }}>
                                            <SecurityIcon sx={{ mb: 0.5, color: '#6b9979' }} />
                                            <Typography variant="h6" fontWeight="bold">
                                                {totalProcessing}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                {t('ropa.area.processings')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Descrição */}
                    {area.description && (
                        <Box sx={{ p: 3, bgcolor: '#f5f8fe' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AssignmentIcon sx={{ mr: 1, color: '#a5b8d4', fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight="bold" color="#6b7999">
                                    {t('ropa.area.description')}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {area.description}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Processos */}
            {!!area.processes && filteredProcesses?.map((itemProcess, index) => (
                <motion.div
                    key={`${itemProcess._id}-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                    <ReportsRopaProcess
                        process={itemProcess}
                        filter={filter}
                        data={data}
                        category={category}
                        legalBase={legalBase}
                        processing={processing}
                        position={position}
                    />
                </motion.div>
            ))}

            {/* Mensagem quando não há processos */}
            {(!filteredProcesses || filteredProcesses.length === 0) && (
                <Paper 
                    elevation={1}
                    sx={{ 
                        p: 3, 
                        textAlign: 'center', 
                        borderRadius: 2,
                        bgcolor: '#f5f5f5',
                        mt: 2
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('ropa.area.noProcesses')}
                    </Typography>
                </Paper>
            )}
        </>
    )
}